import { tap } from "rxjs/operators";
import { PublicHttpClientService } from "./http-client.service";
import { PublicDocumentsService } from "./documents.service";
import { Observable, of } from "rxjs";
import { Injectable } from "@angular/core";
import { AddressData, OnboardingAddress } from "../../interfaces/lead_onboarding/onboarding-address.interface";
import { OnboardingLinkSent } from "../../interfaces/lead_onboarding/onboarding-link-sent.interface";
import { OnboardingIdentity } from "../../interfaces/lead_onboarding/onboarding-identity.interface";
import { OnboardingIncomes } from "../../interfaces/lead_onboarding/onboarding-incomes.interface";
import { OnboardingScheduleCall } from "../../interfaces/lead_onboarding/onboarding-schedule-call.interface";
import { OnboardingCiecData } from "../../interfaces/lead_onboarding/onboarding-ciec-data.interface";
import { OnboardingBankAccountData } from "../../interfaces/lead_onboarding/onboarding-bank-account-data.interface";
import { OnboardingValidatingAddress } from "../../interfaces/lead_onboarding/onboarding-validating-address.interface";
import { OnboardingUploadDocuments } from "../../interfaces/lead_onboarding/onboarding-upload-documents.interface";

@Injectable()
export class PublicLeadOnboardingService {
  constructor(private http: PublicHttpClientService, private _documentsService: PublicDocumentsService) {}

  // # GET /api/public/onboarding?token=
  public getOnboarding(token): Observable<any> {
    return this.http
      .get(`/api/public/onboarding/${token}`)
      .pipe(tap((_) => console.log("PublicLeadOnboardingService.getOnboarding: Ok.")));
  }

  // # PUT /api/public/onboarding/:token
  public updateOnboarding(
    token,
    onboarding:
      | OnboardingLinkSent
      | OnboardingIdentity
      | OnboardingAddress
      | OnboardingValidatingAddress
      | OnboardingIncomes
      | OnboardingScheduleCall
      | OnboardingBankAccountData
      | OnboardingUploadDocuments
  ): Observable<any> {
    const options = {
      onboarding,
    };
    return this.http
      .put(`/api/public/onboarding/${token}`, options)
      .pipe(tap((_) => console.log("PublicLeadOnboardingService.updateOnboarding: Ok.")));
  }

  // # POST /api/onboarding/:token/set_siec
  public setCiec(token, ciec: OnboardingCiecData): Observable<any> {
    const options = {
      onboarding: ciec,
    };
    return this.http
      .post(`/api/public/onboarding/${token}/set_ciec`, options)
      .pipe(tap((_) => console.log("PublicLeadOnboardingService.setSiec: Ok.")));
  }

  // # POST /api/public/onboarding/:token/send_code
  public sendCode(token): Observable<any> {
    const options = {};

    return this.http
      .post(`/api/public/onboarding/${token}/send_code`, options)
      .pipe(tap((_) => console.log("PublicLeadOnboardingService.sendCode: Ok.")));
  }

  // # POST /api/public/onboarding/:token/skip_otp
  public skipOtpVerification(token): Observable<any> {
    const options = {};
    return this.http
      .post(`/api/public/onboarding/${token}/skip_otp`, options)
      .pipe(tap((_) => console.log("PublicLeadOnboardingService.skipOtpVerification: Ok.")));
  }

  // # POST /api/public/onboarding/:token/verify
  public verify(token, code: string): Observable<any> {
    const options = {
      code,
    };
    return this.http
      .post(`/api/public/onboarding/${token}/verify`, options)
      .pipe(tap((_) => console.log("PublicLeadOnboardingService.verify: Ok.")));
  }

  // # POST /api/public/onboarding/:token/review
  public review(token): Observable<any> {
    const options = {};
    return this.http
      .post(`/api/public/onboarding/${token}/review`, options)
      .pipe(tap((_) => console.log("PublicLeadOnboardingService.review: Ok.")));
  }

  // # POST /api/public/onboarding/:token/cancel_review
  public cancelReview(token): Observable<any> {
    const options = {};
    return this.http
      .post(`/api/public/onboarding/${token}/cancel_review`, options)
      .pipe(tap((_) => console.log("PublicLeadOnboardingService.cancelReview: Ok.")));
  }

  // # POST /api/public/onboarding/:token/request_new_link
  public requestNewLink(token): Observable<any> {
    const options = {};
    return this.http
      .post(`/api/public/onboarding/${token}/request_new_link`, options)
      .pipe(tap((_) => console.log("PublicLeadOnboardingService.requestNewLink: Ok.")));
  }

  // # POST /api/public/onboarding/:token/metrics
  public metrics(token, data): Observable<any> {
    const options = { event: data };
    return this.http
      .post(`/api/public/onboarding/${token}/metrics`, options)
      .pipe(tap((_) => console.log("PublicLeadOnboardingService.metrics: Ok.")));
  }

  // # POST /api/public/onboarding/:token/privacy_consent
  public acceptPrivacyPolicy(token: string): Observable<any> {
    return this.http
      .post(`/api/public/onboarding/${token}/privacy_consent`, {})
      .pipe(tap((_) => console.log("PublicLeadOnboardingService.acceptPrivacyPolicy: Ok.")));
  }

  // # POST /api/public/onboarding/:token/track_link_source
  public trackLinkSource(token, source): Observable<any> {
    const options = { source, timestamp: new Date().toISOString() };
    return this.http
      .post(`/api/public/onboarding/${token}/track_link_source`, options)
      .pipe(tap((_) => console.log("PublicLeadOnboardingService.trackLinkSource: Ok.")));
  }

  // # POST /api/public/onboarding/:token/add_address
  public addAddressProof(token, address: AddressData, address_type): Observable<any> {
    const options = {
      address,
      address_type,
    };
    return this.http
      .post(`/api/public/onboarding/${token}/add_address`, options)
      .pipe(tap((_) => console.log("PublicLeadOnboardingService.addAddress: Ok.")));
  }

  // # POST /api/public/onboarding/:token/create_incode_session
  public createIncodeSession(token: string, identification_type: string): Observable<any> {
    const options = {
      proof_type: identification_type,
    };
    return this.http
      .post(`/api/public/onboarding/${token}/create_incode_session`, options)
      .pipe(tap((_) => console.log("PublicLeadOnboardingService.createIncodeSession: Ok.")));
  }

  // # POST /api/public/onboarding/:token/selfie_attempts
  // BE-confirmed one call per selfie attempt (not a consolidated
  // summary), so BE can append each to the full history.
  public reportSelfieAttempt(
    token: string,
    data: { incode_session_id: string; succeeded: boolean; reason: string[] }
  ): Observable<any> {
    return this.http
      .post(`/api/public/onboarding/${token}/selfie_attempts`, data)
      .pipe(tap((_) => console.log("PublicLeadOnboardingService.reportSelfieAttempt: Ok.")));
  }

  // # /api/public/onboarding/working_days/next?date=
  public getNextWorkingDays(date: string): Observable<any> {
    return this.http
      .get(`/api/public/onboarding/working_days/next?date=${date}`)
      .pipe(tap((_) => console.log("PublicLeadOnboardingService.getNextWorkingDay: Ok.")));
  }

  // # /api/public/onboarding/next_working_day?date=
  public getNextWorkingDay(date: string): Observable<any> {
    return this.http
      .get(`/api/public/onboarding/next_working_day?date=${date}`)
      .pipe(tap((_) => console.log("PublicLeadOnboardingService.getNextWorkingDay: Ok.")));
  }

  // # /api/public/onboarding/:token/last_onboarding_info
  public getLastOnboardingInfo(token: string): Observable<any> {
    return this.http
      .get(`/api/public/onboarding/${token}/last_onboarding_info`)
      .pipe(tap((_) => console.log("PublicLeadOnboardingService.getLastOnboardingInfo: Ok.")));
  }

  // # GET /api/public/lead_onboardings/:token/application/bank_accounts
  public getBankAccounts(token: string): Observable<any> {
    return this.http
      .get(`/api/public/onboarding/${token}/application/bank_accounts`)
      .pipe(tap((_) => console.log("PublicLeadOnboardingService.getBankAccounts: Ok.")));
  }

  // # GET /api/public/onboarding/:token/references
  // Application/person are resolved from the Onboarding access token (ADR 0003).
  public getReferences(token: string): Observable<any> {
    return this.http
      .get(`/api/public/onboarding/${token}/references`)
      .pipe(tap((_) => console.log("PublicLeadOnboardingService.getReferences: Ok.")));
  }

  // # POST /api/public/onboarding/:token/references
  // Do not send enumerable application_id / person_id; backend binds from token.
  public createReference(
    token: string,
    reference: {
      name: string;
      is_family: boolean;
      start_time: Date;
      finish_time: Date;
    },
    phone: string
  ): Observable<any> {
    const params = {
      reference,
      phone,
    };
    return this.http
      .post(`/api/public/onboarding/${token}/references`, params)
      .pipe(tap((_) => console.log("PublicLeadOnboardingService.createReference: Ok.")));
  }

  public getCapturedAddress(token): Observable<any> {
    return this.http
      .get(`/api/public/onboarding/${token}/fetch_captured_address`, {})
      .pipe(tap((_) => console.log("PublicLeadOnboardingService.getCapturedAddress: Ok.")));
  }

  // # GET /api/public/documents/:id/download
  public downloadPublicDocument(
    id: number | string,
    resource_id: string | number,
    resource_class = "Application"
  ): Observable<Blob> {
    return this._documentsService.download(id, resource_id, resource_class);
  }

  public notifyExpiredId(token: string): Observable<any> {
    return this.http
      .post(`/api/public/onboarding/${token}/help_for_expired_id`, {})
      .pipe(tap((_) => console.log("PublicLeadOnboardingService.notifyExpiredId: Ok.")));
  }

  public abortByIdExpiredMaxRetries(token: string): Observable<any> {
    return this.http
      .post(`/api/public/onboarding/${token}/id_expired_max_retries`, {})
      .pipe(tap((_) => console.log("PublicLeadOnboardingService.abortByIdExpiredMaxRetries: Ok.")));
  }
}
